import React, { useState } from 'react'
import { matchPath, useLocation } from 'react-router'
import qs from 'query-string'
import styled from 'styled-components'
import dateFormat from 'utils/localizedDateFormat'
import Box, { Text } from 'components/Box'
import Heading from 'components/Heading'
import {
  DocumentInfo as DocumentInfoWrapper,
  DocumentInfoTitle,
  DocumentInfoItemWrapper,
} from './ShareSettingsModal.styles'
import { formatByteCount } from 'utils/formatByteCount'
import {
  GetSharesDocument,
  GetSharesQueryVariables,
  ShareListItemFragment,
  ShareUpdateMutation,
  useShareUpdateMutation,
} from 'gqlTypes'
import Checkbox from 'components/Checkbox'
import { useToast } from 'containers/ToastManager'
import Spinner from 'components/Spinner'
import { VersionForSettingsModal } from './types'

import { removeFromPaginated } from 'utils'
import { routes } from 'constants/routes'

/**
 * TYPES
 */
interface DocumentInfoItemProps {
  title: string
  text?: string | null
}

export interface ShareForSettingsModal
  extends Pick<
    ShareListItemFragment,
    'identifier' | 'createdAt' | 'libraryEnabled' | 'workspace'
  > {}

export interface DocumentInfoProps {
  share: ShareForSettingsModal
  version: VersionForSettingsModal
}

/**
 * STYLES
 */
const SpinnerWrapper = styled.div`
  position: absolute;
  margin-left: -32px;
`

/**
 * COMPONENTS
 */
const DocumentInfoItem = ({ title, text }: DocumentInfoItemProps) => {
  if (!text) return null

  return (
    <DocumentInfoItemWrapper>
      <Heading.H5 mt={4} mb={0} color="text.dark.tertiary">
        {title}
      </Heading.H5>
      <Text>{text}</Text>
    </DocumentInfoItemWrapper>
  )
}

const DocumentInfo = ({ share, version }: DocumentInfoProps) => {
  const { createdAt, workspace } = share
  const { currentVersion, latestVersion } = version
  const sizeInBytes = currentVersion?.document?.size
  const lastUpdate = latestVersion?.createdAt

  const location = useLocation()
  const [libraryEnabled, setLibraryEnabled] = useState(share.libraryEnabled)

  const [
    updateShare,
    { loading: updateLibraryLoading },
  ] = useShareUpdateMutation({
    redirectErrors: false,
    awaitRefetchQueries: true,
    onError: 'unsafe-throw-exception',
    update: cache => {
      // Remove all possible awareness of this Share from the Library filter query
      removeFromPaginated(
        cache,
        { __typename: 'Share', identifier: share.identifier },
        key => key.includes('"filter":"LIBRARY"')
      )
    },

    refetchQueries: ({ data }) => {
      const typedData = data as ShareUpdateMutation

      if (!typedData?.shareUpdate?.successful) {
        return []
      }

      const isLibraryView = matchPath(location.pathname, {
        path: routes.WORKSPACE_LIBRARIES.template(),
        exact: true,
      })

      if (!isLibraryView) {
        return []
      }

      const name = qs.parse(location.search)?.search

      return [
        {
          query: GetSharesDocument,
          variables: {
            identifier: workspace.identifier,
            after: null,
            search: {
              name,
              filter: 'LIBRARY',
            },
          } as GetSharesQueryVariables,
        },
      ]
    },
  })

  const { showToast } = useToast()

  const handleChangeUseAsLibrary = async () => {
    try {
      const { data: updateShareData } = await updateShare({
        variables: {
          identifier: share.identifier,
          share: {
            libraryEnabled: !libraryEnabled,
          },
        },
      })

      const errors = updateShareData?.shareUpdate?.errors
      if (errors && errors.length > 0) {
        throw new Error(errors[0]!.message)
      }

      setLibraryEnabled(!libraryEnabled)
    } catch (error) {
      showToast(error.message, 'negative')
    }
  }
  return (
    <>
      <DocumentInfoTitle>Use as Library</DocumentInfoTitle>
      <Box mt={3}>
        {updateLibraryLoading && (
          <SpinnerWrapper>
            <Spinner primary />
          </SpinnerWrapper>
        )}
        <Checkbox
          checked={libraryEnabled || false}
          onChange={handleChangeUseAsLibrary}
          help="Users can download updates and will be notified of changes">
          Share Symbols, Styles and Color Variables
        </Checkbox>
      </Box>

      <Box height={1} bg="border.dark.base" mt={5} />

      <DocumentInfoTitle>Document Info</DocumentInfoTitle>
      <DocumentInfoWrapper>
        <DocumentInfoItem
          title="Created On"
          text={dateFormat(new Date(createdAt as string))}
        />
        <DocumentInfoItem title="Located In" text={workspace.name} />

        <DocumentInfoItem
          title="Last Updated On"
          text={dateFormat(new Date(lastUpdate as string))}
        />
        <DocumentInfoItem
          title="Color Profile"
          text={currentVersion?.document?.colorspace || 'Unmanaged'}
        />
        <DocumentInfoItem
          title="File Size"
          text={formatByteCount(sizeInBytes as number)}
        />
      </DocumentInfoWrapper>
    </>
  )
}

export default DocumentInfo
